import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface Analytics {
  overview: {
    totalAll: number; totalPeriod: number; open: number; inProgress: number
    waitingUser: number; resolved: number; closed: number
    avgResponseTime: number; avgResolutionTime: number
    avgCSAT: string | null; csatCount: number
  }
  agentStats: Array<{
    id: number; name: string; totalAssigned: number; periodAssigned: number
    resolved: number; avgResponseMin: number | null; avgCSAT: string | null; ratingCount: number
  }>
  categoryBreakdown: Record<string, number>
  priorityBreakdown: Record<string, number>
}

const formatMin = (m: number) => {
  if (!m || isNaN(m) || m === 0) return '—'
  if (m < 60) return `${m}m`
  if (m < 1440) return `${Math.round(m / 60)}h ${m % 60}m`
  return `${Math.floor(m / 1440)}d ${Math.round((m % 1440) / 60)}h`
}

const SupportAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState('30d')
  const [error, setError] = useState('')

  useEffect(() => { loadAnalytics() }, [period])

  const loadAnalytics = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_analytics', data: { period } })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setAnalytics(json.data)
    } catch (err: any) { setError(err.message) }
    setLoading(false)
  }

  const StatCard = ({ label, value, sub, color = 'text-gray-900' }: { label: string; value: string | number; sub?: string; color?: string }) => (
    <div className="bg-white border rounded-lg p-4">
      <p className="text-sm text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-sm text-white mt-1">{sub}</p>}
    </div>
  )

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div></div>
  if (!analytics) return <div className="text-center py-8 text-gray-500">Failed to load analytics</div>

  const o = analytics.overview
  const catEntries = Object.entries(analytics.categoryBreakdown).sort((a, b) => b[1] - a[1])
  const __priEntries = Object.entries(analytics.priorityBreakdown).sort((a, b) => b[1] - a[1])
  const maxCat = catEntries.length > 0 ? catEntries[0][1] : 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Support Analytics</h3>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {['7d', '30d', '90d'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded text-sm ${period === p ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500'}`}>{p}</button>
          ))}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Total Tickets" value={o.totalAll} sub={`${o.totalPeriod} in period`} />
        <StatCard label="Open" value={o.open} color="text-blue-600" />
        <StatCard label="In Progress" value={o.inProgress} color="text-yellow-600" />
        <StatCard label="Waiting User" value={o.waitingUser} color="text-orange-600" />
        <StatCard label="Avg Response" value={formatMin(o.avgResponseTime)} color="text-purple-600" />
        <StatCard label="Avg Resolution" value={formatMin(o.avgResolutionTime)} color="text-green-600" />
      </div>

      {/* CSAT */}
      {o.avgCSAT && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⭐</span>
            <div>
              <p className="text-xl font-bold text-gray-900">{o.avgCSAT}/5 CSAT</p>
              <p className="text-sm text-gray-500">{o.csatCount} rating{o.csatCount !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Performance */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Agent Performance</h4>
          {analytics.agentStats.length === 0 ? <p className="text-sm text-gray-500">No agents</p> : (
            <div className="space-y-3">
              {analytics.agentStats.map(a => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{a.name}</p>
                    <p className="text-sm text-gray-500">{a.totalAssigned} total · {a.resolved} resolved</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">{a.avgResponseMin !== null ? formatMin(a.avgResponseMin) : '—'} <span className="text-sm text-white">avg resp</span></p>
                    {a.avgCSAT && <p className="text-sm text-gray-500">⭐ {a.avgCSAT} ({a.ratingCount})</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category & Priority Breakdown */}
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">By Category (period)</h4>
            <div className="space-y-2">
              {catEntries.map(([cat, count]) => (
                <div key={cat} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-24 truncate">{cat}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className="bg-yellow-500 h-full rounded-full" style={{ width: `${(count / maxCat) * 100}%` }}></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-8 text-right">{count}</span>
                </div>
              ))}
              {catEntries.length === 0 && <p className="text-sm text-white">No data for period</p>}
            </div>
          </div>


          <div className="bg-white border rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">By Priority (period)</h4>
            <div className="flex gap-3">
              {['urgent', 'high', 'medium', 'low'].map(p => {
                const count = analytics.priorityBreakdown[p] || 0
                const colors: Record<string, string> = { urgent: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-yellow-500', low: 'bg-gray-400' }
                return (
                  <div key={p} className="flex-1 text-center">
                    <div className={`${colors[p]} text-white rounded-lg py-3 text-xl font-bold`}>{count}</div>
                    <p className="text-sm text-gray-500 mt-1 capitalize">{p}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupportAnalytics